import { useEffect, useState } from 'react';
import { Autocomplete, Box, Button, Popover, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ScribanExprPopoverProps {
  /** Anchor element; the popover is open while this is non-null. */
  anchorEl: HTMLElement | null;
  expr: string;
  /** Expression paths from the document type's sample data, offered as suggestions. */
  options: string[];
  onClose: () => void;
  onSave: (expr: string) => void;
  onRemove: () => void;
}

/**
 * Edit dialog for a placeholder chip in the visual template editor. Free text is allowed —
 * typos surface through the strict-variables live preview, the same channel the code view uses.
 */
export const ScribanExprPopover = ({
  anchorEl,
  expr,
  options,
  onClose,
  onSave,
  onRemove,
}: ScribanExprPopoverProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(expr);
  const open = Boolean(anchorEl);

  // Re-seed the draft each time the popover opens for a (possibly different) chip.
  useEffect(() => {
    if (open) setValue(expr);
  }, [open, expr]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ p: 1.5, width: 320, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Autocomplete
          freeSolo
          options={options}
          value={value}
          onInputChange={(_, next) => setValue(next)}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              size="small"
              label={t('documentTemplate.expression', 'Expression')}
              slotProps={{
                input: { ...params.InputProps, sx: { fontFamily: 'monospace', fontSize: 13 } },
              }}
            />
          )}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" color="error" onClick={onRemove} sx={{ textTransform: 'none' }}>
            {t('common.remove', 'Remove')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!value.trim()}
            onClick={() => onSave(value.trim())}
            sx={{ textTransform: 'none' }}
          >
            {t('common.save', 'Save')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
